import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faChevronDown,
  faPlane,
} from "@fortawesome/free-solid-svg-icons";
import { config } from "../config";
import { airlines } from "../data/airlines";
import FlightDetailsModal from "./FlightDetailsModal";

function FlightCard({ flight }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showStopoverTooltip, setShowStopoverTooltip] = useState(false);
  const navigate = useNavigate();
  const airline = airlines[flight.operatorCode];
  const isReturn = flight.return_date && flight.return_time;

  const handleNextStep = () => {
    navigate(
      `/booking/details?st=f18f7517231246cb979cb4ab916d6f4d&flights=${flight.id}`
    );
  };

  return (
    <>
      <div className="bg-white rounded-lg p-4 md:p-6 mb-4 shadow-none border-gray-200 border">
        <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0">
          {/* Airline Info */}
          <div className="flex items-center justify-between md:justify-start md:w-auto">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-lg p-1.5 flex items-center justify-center border border-gray-100">
                <img
                  src={airline.logo}
                  alt={airline.name}
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <p className="text-gray-700 text-sm md:text-base">
                  {airline.name}
                </p>
              </div>
            </div>
            {/* Price shown on mobile only */}
            <div className="md:hidden">
              <p className={`text-xl font-bold text-${config.primaryColor}`}>
                £{flight.price}
              </p>
            </div>
          </div>

          {/* Flight Details */}
          <div className="flex-grow md:px-8">
            {/* Outbound Flight */}
            <div className="flex flex-col space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-center">
                  <p className="text-sm text-gray-500">
                    {flight.outbound_date}
                  </p>
                  <p className="text-lg md:text-xl font-bold">
                    {flight.outbound_time}
                  </p>
                  <p className="text-sm">{flight.origin}</p>
                </div>

                <div className="flex flex-col items-center flex-grow px-4">
                  <p className="text-xs md:text-sm text-gray-500">
                    {flight.duration}
                  </p>
                  <div className="relative w-full">
                    <div className="border-t border-gray-300 w-full absolute top-1/2"></div>
                    <FontAwesomeIcon
                      icon={faPlane}
                      className={`text-${config.primary} absolute -top-2 right-0`}
                    />
                  </div>
                  <div
                    className="relative"
                    onMouseEnter={() => setShowStopoverTooltip(true)}
                    onMouseLeave={() => setShowStopoverTooltip(false)}
                  >
                    <p className="text-xs md:text-sm text-gray-500">
                      {flight.stops || "Non-stop"}
                    </p>
                    {showStopoverTooltip && flight.stopover && (
                      <div className="absolute z-10 bg-gray-800 text-white px-3 py-2 rounded-md text-xs md:text-sm -bottom-10 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                        Stopover at {flight.stopover.airport}
                      </div>
                    )}
                  </div>
                </div>

                <div className="text-center">
                  <p
                    className={`text-sm ${
                      flight.outbound_arrival_date !== flight.outbound_date
                        ? "text-red-400"
                        : "text-gray-500"
                    }`}
                  >
                    {flight.outbound_arrival_date}
                  </p>
                  <p className="text-lg md:text-xl font-bold">
                    {flight.outbound_arrival_time}
                  </p>
                  <p className="text-sm">{flight.destination}</p>
                </div>
              </div>

              {/* Return Flight - Only shown if it's a return flight */}
              {isReturn && (
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="text-center">
                    <p className="text-sm text-gray-500">
                      {flight.return_date}
                    </p>
                    <p className="text-lg md:text-xl font-bold">
                      {flight.return_time}
                    </p>
                    <p className="text-sm">{flight.destination}</p>
                  </div>

                  <div className="flex flex-col items-center flex-grow px-4">
                    <p className="text-xs md:text-sm text-gray-500">
                      {flight.return_duration || flight.duration}
                    </p>
                    <div className="relative w-full">
                      <div className="border-t border-gray-300 w-full absolute top-1/2"></div>
                      <FontAwesomeIcon
                        icon={faPlane}
                        className={`text-${config.primaryColor} absolute -top-2 right-0`}
                      />
                    </div>
                    <p className="text-xs md:text-sm text-gray-500">
                      {flight.return_stops || "Non-stop"}
                    </p>
                  </div>

                  <div className="text-center">
                    <p className="text-sm text-gray-500">
                      {flight.return_date}
                    </p>
                    <p className="text-lg md:text-xl font-bold">
                      {flight.return_arrival_time}
                    </p>
                    <p className="text-sm">{flight.origin}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Price and Buttons - Hidden on mobile, shown on desktop */}
          <div className="hidden md:flex md:flex-col md:items-end space-y-2">
            <p className={`text-2xl font-bold text-${config.primaryColor}`}>
              £{flight.price}
            </p>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-gray-200 text-gray-700 px-6 py-2 rounded-full hover:bg-gray-300 transition-colors flex items-center"
            >
              Details{" "}
              <FontAwesomeIcon
                icon={faChevronDown}
                size="sm"
                className="ml-2"
              />
            </button>
            <button
              onClick={handleNextStep}
              className={`bg-${config.primaryColor} text-white rounded-full flex items-center justify-center px-6 py-2 hover:bg-purple-700 transition-colors`}
            >
              Next step
              <FontAwesomeIcon icon={faArrowRight} size="sm" className="ml-2" />
            </button>
          </div>

          {/* Mobile Buttons */}
          <div className="flex md:hidden justify-between items-center space-x-2 pt-4 border-t border-gray-100">
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-full hover:bg-gray-300 transition-colors flex items-center justify-center text-sm"
            >
              Details{" "}
              <FontAwesomeIcon
                icon={faChevronDown}
                size="sm"
                className="ml-2"
              />
            </button>
            <button
              onClick={handleNextStep}
              className={`flex-1 bg-${config.primaryColor} text-white rounded-full flex items-center justify-center px-4 py-2 hover:bg-purple-700 transition-colors text-sm`}
            >
              Next step
              <FontAwesomeIcon icon={faArrowRight} size="sm" className="ml-2" />
            </button>
          </div>
        </div>
      </div>

      <FlightDetailsModal
        flight={flight}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
}

export default FlightCard;
